import {Card} from 'src/design/atoms/Card';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import type {InspectionResponse, SiteResponse} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {Subtitle} from 'src/design/atoms/Typography';
import {InlineStat} from 'src/design/molecules/InlineStat';
import {InspectionStateEnumPill} from 'src/shared/constants/PillConstants';
import {formatUtcCalendarDate} from 'src/shared/utils/formatters';

export interface InspectionMyOverviewSummaryProps {
  inspection: InspectionResponse;
  site?: SiteResponse;
}

export const InspectionMyOverviewSummary: React.FC<
  InspectionMyOverviewSummaryProps
> = ({inspection, site}) => {
  const dueLabel = formatUtcCalendarDate(inspection.dueAt ?? undefined);
  const addressLine = [site?.address, site?.city].filter(Boolean).join(', ');
  const sublineParts = [
    inspection.clientName ? `Client: ${inspection.clientName}` : undefined,
    addressLine || undefined,
  ].filter(Boolean);
  const buildingLabel = `${inspection.buildingCount} building${
    inspection.buildingCount === 1 ? '' : 's'
  }`;

  return (
    <Card shadow="sm" withBorder padding="lg">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <Subtitle
          text={`${inspection.siteName} — ${inspection.inspectionTypeName}`}
          style={{flex: 1}}
        />
        <Pill {...InspectionStateEnumPill(inspection.state)} />
      </Group>

      {sublineParts.length > 0 && (
        <Text size="sm" c="dimmed" mt={4}>
          {sublineParts.join(' · ')}
        </Text>
      )}

      <Divider my="md" color="var(--mantine-color-gray-2)" />

      <SimpleGrid cols={{base: 2, sm: 4}} spacing="xl" verticalSpacing="md">
        <InlineStat
          label="Devices"
          value={inspection.scopedDeviceCount.toLocaleString()}
        />
        <InlineStat
          label="Floors"
          labelDetail={buildingLabel}
          value={inspection.floorCount}
        />
        <InlineStat label="Due date" value={dueLabel} />
        <InlineStat
          label="Standards"
          value={inspection.inspectionTypeStandards || '—'}
        />
      </SimpleGrid>
    </Card>
  );
};
